import React, { useState, useEffect, useCallback } from "react";
import { useHotel } from "../contexts/HotelContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api, { createRedirectToken } from "../services/api";
import { detectBrandByDomain } from "../services/brandApi";
import { useTrialDuration } from "../hooks/useTrialDuration";
import { formatTrialDays } from "../utils/trialDays";
import Footer from "../components/Footer";

/**
 * Email verification banner shown when login is blocked due to unverified email.
 * AC-12: Shows message + resend verification link.
 */
const EmailNotVerifiedBanner: React.FC<{
  unverifiedEmail: string;
  onDismiss: () => void;
}> = ({ unverifiedEmail, onDismiss }) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const handleResend = useCallback(async () => {
    if (resendLoading) return;
    setResendLoading(true);
    setResendMessage(null);
    setResendError(null);

    try {
      await api.post("/auth/resend-verification", {
        email: unverifiedEmail,
      });
      setResendMessage("Link weryfikacyjny został wysłany.");
    } catch (error: any) {
      if (error.response?.status === 429) {
        setResendError("Poczekaj chwilę przed ponownym wysłaniem.");
      } else {
        setResendError("Wystąpił błąd. Spróbuj ponownie.");
      }
    } finally {
      setResendLoading(false);
    }
  }, [unverifiedEmail, resendLoading]);

  return (
    <div
      role="alert"
      className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4"
    >
      <div className="flex items-start">
        <svg
          className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <div className="flex-1">
          <p className="text-sm text-yellow-800">
            Konto <strong>{unverifiedEmail}</strong> wymaga potwierdzenia
            adresu email. Nie masz linku?
          </p>

          {resendMessage && (
            <p className="text-sm text-green-600 mt-2" role="status">
              {resendMessage}
            </p>
          )}

          {resendError && (
            <p className="text-sm text-red-600 mt-2" role="alert">
              {resendError}
            </p>
          )}

          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              onClick={handleResend}
              disabled={resendLoading}
              className="text-sm font-medium text-primary-600 hover:text-primary-700 disabled:text-gray-400 transition-colors duration-200"
            >
              {resendLoading ? "Wysyłanie..." : "Wyślij ponownie"}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors duration-200"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Login: React.FC = () => {
  const { currentHost } = useHotel();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [useMagicLink, setUseMagicLink] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [brandName, setBrandName] = useState("");
  const [brandId, setBrandId] = useState<string | null>(null);
  // AC-12: Email not verified state
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  // AC-F1: Fetch trial duration from public catalog API
  const { days: trialDurationDays } = useTrialDuration(brandId);

  useEffect(() => {
    // Redirect authenticated users away from login page
    const token = localStorage.getItem("token");
    if (token) {
      const from = location.state?.from?.pathname || "/";
      navigate(from, { replace: true });
    }
  }, [navigate, location]);

  useEffect(() => {
    // Fetch brand name from backend based on current domain
    const fetchBrandName = async () => {
      try {
        const brand = await detectBrandByDomain(currentHost);
        setBrandName(brand.displayName || "Hotel B2B");
        if (brand.id) {
          setBrandId(brand.id);
        }
      } catch (error) {
        console.error("Failed to fetch brand name:", error);
        // Keep default fallback value
      }
    };

    fetchBrandName();
  }, [currentHost]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage("");
    setUnverifiedEmail(null);

    try {
      if (useMagicLink) {
        await api.post("/auth/login", { email });
        setMessage("Link magiczny został wysłany na Twój email!");
      } else {
        const response = await api.post("/auth/login-password", {
          email,
          password,
        });
        localStorage.setItem("token", response.data.access_token);

        // Check if we need to redirect to tenant subdomain
        try {
          const redirectData = await createRedirectToken();

          if (redirectData.alreadyOnCorrectDomain) {
            // User is already on correct domain - go to dashboard
            window.location.href = "/";
          } else {
            // Redirect to correct tenant domain
            window.location.href = `${redirectData.redirectUrl}/login-redirect?rt=${redirectData.redirectToken}`;
          }
          return;
        } catch (err) {
          console.error("Failed to check tenant redirect:", err);
          // Continue with normal flow on error
          window.location.href = "/";
        }
      }
    } catch (error: any) {
      // AC-12: Handle EMAIL_NOT_VERIFIED response (403) for both login methods
      const responseData = error.response?.data;
      if (
        error.response?.status === 403 &&
        responseData?.code === "EMAIL_NOT_VERIFIED"
      ) {
        setUnverifiedEmail(responseData.email || email);
        setIsLoading(false);
        return;
      }

      if (useMagicLink) {
        setMessage(
          "Błąd podczas wysyłania linku magicznego. Spróbuj ponownie."
        );
      } else {
        const errorMessage = error.response?.data?.message;
        setMessage(
          typeof errorMessage === "string"
            ? errorMessage
            : "Nieprawidłowy email lub hasło."
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center mb-6">
            <svg
              className="h-8 w-8 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-neutral-900">
            Zaloguj się {brandName && <span>do {brandName}</span>}
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            {useMagicLink
              ? "Wprowadź swój służbowy email, aby otrzymać link magiczny"
              : "Wprowadź swój email i hasło, aby się zalogować"}
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
          {/* AC-12: Email not verified banner */}
          {unverifiedEmail && (
            <EmailNotVerifiedBanner
              unverifiedEmail={unverifiedEmail}
              onDismiss={() => setUnverifiedEmail(null)}
            />
          )}

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Adres email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Wprowadź swój służbowy email"
              />
            </div>

            {!useMagicLink && (
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-neutral-700 mb-2"
                >
                  Hasło
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Wprowadź swoje hasło"
                />
                <div className="mt-2 text-right">
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200"
                  >
                    Zapomniałeś hasła?
                  </Link>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                id="useMagicLink"
                name="useMagicLink"
                type="checkbox"
                checked={useMagicLink}
                onChange={(e) => setUseMagicLink(e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-neutral-300 rounded"
              />
              <label
                htmlFor="useMagicLink"
                className="ml-2 block text-sm text-neutral-700"
              >
                Użyj linku magicznego zamiast tego
              </label>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                aria-busy={isLoading}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 px-4 rounded-lg shadow-sm transition-colors duration-200"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    {useMagicLink ? "Wysyłanie..." : "Logowanie..."}
                  </div>
                ) : useMagicLink ? (
                  "Wyślij Link Magiczny"
                ) : (
                  "Zaloguj się"
                )}
              </button>
            </div>

            {message && (
              <div
                role="alert"
                className={`text-center text-sm font-medium ${
                  typeof message === "string" &&
                  (message.includes("sent") || message.includes("wysłany"))
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              >
                {message}
              </div>
            )}
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-neutral-600">
              Nie masz konta?{" "}
              <a
                href="/register"
                className="font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200"
              >
                {trialDurationDays !== null
                  ? `Rozpocznij ${formatTrialDays(trialDurationDays)} darmowy okres próbny`
                  : "Rozpocznij darmowy okres próbny"}
              </a>
            </p>
          </div>
        </div>
      </div>
      </div>
      <div className="container mx-auto px-4">
        <Footer />
      </div>
    </div>
  );
};

export default Login;
